import React, {useEffect, useState} from 'react';
import {useDispatch} from 'react-redux';
import {Link} from 'react-router-dom';

import {ErrorBoundary} from '@sentry/react';
import {MainHeader} from 'src/components/main-header';
import {useSystemAccounts} from 'src/hooks/account/system-accounts';
import {useGetDeprecatedAccessModules} from 'src/hooks/deprecatedAccessModules/useGetDeprecatedAccessModules';
import {
    useAccountEntityTypes,
    useAccountManagerTypes,
    useAccountStatuses,
    useAvailableAdminRights,
    useBusinessLines,
    useCountries,
    useCurrencies,
    useDateFormats,
    useDecimalFormats,
    useEntitlementNamespaces,
    useLicenseCommonLimits,
    useLicenseCrmModules,
    useLicenseDataModules,
    useLicenseFeatureModules,
    useLicenseFeatures,
    useLicenseTypes,
    usePaperFormats,
    useSearchLimits,
    useSymbolPositions,
    useThousandFormats,
    useUserStatuses,
    useUserTypes,
} from 'src/hooks/dictionaries';
import {
    useAlertFrequencyTypes,
    useSavedListTypes,
    useSavedSearchTypes,
} from 'src/hooks/dictionaries/useAlertDictionaries';
import {useCanadianProvinces} from 'src/hooks/dictionaries/useCanadianProvinces';
import {useLayoutTypes} from 'src/hooks/dictionaries/useLayoutDictionaries';
import {useUsStates} from 'src/hooks/dictionaries/useUsStates';
import {useEventTracking} from 'src/hooks/useEventTracking';
import {useConfig} from 'src/modules/config';
import {UserCorner} from 'src/modules/main/components/user-corner';
import {getAdminShortInfo} from 'src/openapi/admin/apis/GetAdminShortInfoApi';
import {getManagedAccounts} from 'src/openapi/admin/apis/GetManagedAccountsApi';
import {getUserLocale} from 'src/openapi/admin/apis/GetUserLocaleApi';
import {Router} from 'src/router/router';

import {Group, Item} from '@pb/kit-component-group';
import {Icon, Icons} from '@pb/kit-component-icon';
import Loading from '@pb/kit-component-loading';
import {PageHeader, PageLogo, PageMain} from '@pb/kit-component-page-v2';
import {StickyElement} from '@pb/kit-component-sticky-element';

import {DefaultAppFooter} from './components/default-footer.component';
import {useSentry} from './use-sentry';

import {logoIcon, main, mainHeader, mainStickyContainer} from './main.module.styl';

import type {ApiDispatchForToolkit} from 'src/openapi/admin/types';


export const Main = () => {
    const apiDispatch = useDispatch<ApiDispatchForToolkit>();

    const [isLoading, setIsLoading] = useState(true);
    const {loadSystemAccounts} = useSystemAccounts();
    useSentry();

    const {searchCriteriaOpen} = useEventTracking();

    const {loadAccountStatuses} = useAccountStatuses();
    const {loadAccountEntityTypes} = useAccountEntityTypes();
    const {loadLicenseTypes} = useLicenseTypes();
    const {loadCountries} = useCountries();
    const {loadAccountManagerTypes} = useAccountManagerTypes();
    const {loadUserTypes} = useUserTypes();
    const {loadUserStatuses} = useUserStatuses();
    const {loadLicenseDataModules} = useLicenseDataModules();
    const {loadLicenseFeaturesModules} = useLicenseFeatureModules();
    const {loadLicenseFeatures} = useLicenseFeatures();
    const {load: loadDeprecatedAccessModules} = useGetDeprecatedAccessModules();
    const {loadLicenseCommonLimits} = useLicenseCommonLimits();
    const {loadSearchLimits} = useSearchLimits();
    const {loadBusinessLines} = useBusinessLines();
    const {loadDateFormats} = useDateFormats();
    const {loadDecimalFormats} = useDecimalFormats();
    const {loadPaperFormats} = usePaperFormats();
    const {loadCurrencies} = useCurrencies();
    const {loadSymbolPositions} = useSymbolPositions();
    const {loadThousandFormats} = useThousandFormats();
    const {loadAvailableAdminRights} = useAvailableAdminRights();
    const {loadUsStates} = useUsStates();
    const {loadCanadianProvinces} = useCanadianProvinces();
    const {loadLicenseCrmModules} = useLicenseCrmModules();

    const {loadServerTimeZone} = useConfig();
    const {loadNamespaces} = useEntitlementNamespaces();

    const {loadAlertFrequencyTypes} = useAlertFrequencyTypes();
    const {loadSavedSearchTypes} = useSavedSearchTypes();
    const {loadSavedListTypes} = useSavedListTypes();
    const {loadLayoutTypes} = useLayoutTypes();

    useEffect(() => {
        const loadMandatoryData = async () => {
            const getAdminInfo = async () => {
                const response = await apiDispatch(getAdminShortInfo());
                if (response.payload.success) {
                    apiDispatch(getManagedAccounts({accountManagerId: response.payload.data.userId}));
                    apiDispatch(getUserLocale({userId: response.payload.data.userId}));
                }
            };

            const mandatoryCalls = [
                getAdminInfo,
                loadSystemAccounts,

                loadAccountStatuses,
                loadAccountEntityTypes,
                loadLicenseTypes,
                loadCountries,
                loadAccountManagerTypes,
                loadUserTypes,
                loadUserStatuses,
                loadLicenseDataModules,
                loadLicenseFeaturesModules,
                loadLicenseFeatures,
                loadDeprecatedAccessModules,
                loadLicenseCommonLimits,
                loadSearchLimits,
                loadBusinessLines,
                loadDateFormats,
                loadDecimalFormats,
                loadPaperFormats,
                loadCurrencies,
                loadSymbolPositions,
                loadThousandFormats,
                loadAvailableAdminRights,
                loadUsStates,
                loadCanadianProvinces,

                loadLicenseCrmModules,

                loadServerTimeZone,
                loadNamespaces,

                loadAlertFrequencyTypes,
                loadSavedSearchTypes,
                loadSavedListTypes,
                loadLayoutTypes,
            ];

            setIsLoading(true);
            for (const call of mandatoryCalls) {
                await call();
            }
            setIsLoading(false);
        };

        loadMandatoryData();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return !isLoading ? (
        <div className={main}>
            <StickyElement className={mainStickyContainer}>
                <PageHeader>
                    <Item grow={1}>
                        <Group justify="space-between" className={mainHeader} alignItems="center">
                            <Link
                                to="/"
                                onClick={() => {
                                    searchCriteriaOpen({place: 'Logo'});
                                }}>
                                <PageLogo>
                                    <Icon type={Icons.PITCHBOOK_LOGO_V2} className={logoIcon}/>
                                </PageLogo>
                            </Link>
                            <UserCorner/>
                        </Group>
                    </Item>
                </PageHeader>
            </StickyElement>
            <MainHeader/>
            <PageMain>
                <ErrorBoundary>
                    <Router/>
                </ErrorBoundary>
            </PageMain>
            <DefaultAppFooter/>
        </div>
    ) : <Loading fast={true}/>;
};
